import * as React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import {
  addFamilySharing,
  AddFamilySharing,
  ApiError,
  userEntersEmail,
  UserEntersEmail,
} from '../actions/Actions';
import InputField from '../input-components/InputField';
import { validateEmail, validatePhoneNumber } from '../validators';
import { trackPulseEngagementEvent } from '../PulseUtils';
import {
  AlignItems,
  BackgroundColor,
  BorderColor,
  BorderWidth,
  BoxShadow,
  Button,
  classNames,
  Cursor,
  Display,
  FlexDirection,
  FontSize,
  FontWeight,
  Icon,
  IconName,
  JustifyContent,
  Margin,
  Padding,
  TextColor,
  TextDecoration,
} from '@snoam/pinata';
import { IProductInfo, IState, IUserInfo } from '../CustomTypes';
import { getStorageData } from '../fastflowUtils';

export interface IActionProps {
  userEntersEmail: UserEntersEmail;
  addFamilySharing: AddFamilySharing;
}

export interface IStateProps {
  userInfo: IUserInfo;
  productInfo: IProductInfo;
}

export interface IProps extends IStateProps, IActionProps {
  newspaperName: string;
}

enum ShareMethods {
  EMAIL = 'EMAIL',
  SMS = 'SMS',
}

const styleClass = {
  clickableHeading: classNames(
    Cursor.CURSOR_POINTER,
    Display.FLEX,
    JustifyContent.JUSTIFY_AROUND,
    FontWeight.FONT_BOLD,
    FontSize.TEXT_LG,
    Margin.MY_4,
    BoxShadow.SHADOW_BORDER_NEUTRAL_1,
  ),
  container: classNames(Padding.P_2, Margin.MY_3, BackgroundColor.BG_WHITE),
  errorText: classNames(
    TextColor.TEXT_NEGATIVE,
    FontWeight.FONT_BOLD,
    Margin.MY_2,
    Margin.MX_4,
  ),
  headingText: classNames(FontSize.MD_TEXT_LG, FontSize.XS_TEXT_SM),
  ingress: classNames(Margin.MY_4),
  invitationBox: {
    root: classNames(
      Margin.MT_2,
      Display.FLEX,
      FlexDirection.FLEX_ROW,
      JustifyContent.JUSTIFY_BETWEEN,
      AlignItems.ITEMS_CENTER,
    ),
    selectBox: (active: boolean) =>
      classNames(
        BorderWidth.BORDER_1,
        FontSize.XS_TEXT_XS,
        FontSize.MD_TEXT_BASE,
        BorderColor.BORDER_NEUTRAL_2,
        Cursor.CURSOR_POINTER,
        FontWeight.FONT_BOLD,
        Padding.P_4,
        {
          [BackgroundColor.BG_PRIMARY_DARK]: active,
          [TextColor.TEXT_PRIMARY_LIGHT]: active,
        },
      ),
    selectBoxContainer: classNames(Display.FLEX, FlexDirection.FLEX_ROW),
    text: classNames(
      FontSize.MD_TEXT_BASE,
      FontSize.XS_TEXT_SM,
      FontWeight.FONT_BOLD,
    ),
  },
  linkText: classNames(TextDecoration.UNDERLINE),
  successMessage: classNames(Margin.MB_4),
};

export class FamilySharing extends React.Component<
  IProps,
  {
    alreadyShared: boolean;
    clicked: boolean;
    email: string;
    shared: boolean;
    validationError: boolean;
    formSubmitted: boolean;
    cellPhone: string;
    shareMethod: ShareMethods;
  }
> {
  constructor(props: IProps) {
    super(props);
    this.showContentIfClicked = this.showContentIfClicked.bind(this);
    this.headingClicked = this.headingClicked.bind(this);
    this.shareProduct = this.shareProduct.bind(this);
    this.trackSharedToFamily = this.trackSharedToFamily.bind(this);
    this.state = {
      alreadyShared: false,
      cellPhone: '',
      clicked: false,
      email: '',
      formSubmitted: false,
      shareMethod: ShareMethods.SMS,
      shared: false,
      validationError: false,
    };
  }

  public render() {
    const { clicked } = this.state;
    return (
      <div className={styleClass.container}>
        <div
          className={styleClass.clickableHeading}
          onClick={this.headingClicked}
        >
          <span>
            <Icon name={IconName.SHARED} />
          </span>
          <span className={styleClass.headingText}>
            Del ditt abonnement gratis
          </span>
          <span>
            {clicked ? (
              <Icon name={IconName.MINUS} />
            ) : (
              <Icon name={IconName.PLUS} />
            )}
          </span>
        </div>
        {this.showContentIfClicked()}
      </div>
    );
  }

  private onChangeShareMethod(method: ShareMethods) {
    if (method === ShareMethods.SMS) {
      this.setState({ email: '' });
    }
    if (method === ShareMethods.EMAIL) {
      this.setState({ cellPhone: '' });
    }
    this.setState({ shareMethod: method });
  }

  private showContentIfClicked() {
    const {
      alreadyShared,
      clicked,
      email,
      shared,
      cellPhone,
      validationError,
      shareMethod,
    } = this.state;
    const { newspaperName } = this.props;
    if (!clicked) {
      return;
    }

    if (alreadyShared) {
      return (
        <p>
          Du har allerede delt ditt digitalabonnement. Du kan endre hvem du
          ønsker å dele det med på Min Side.
        </p>
      );
    }

    if (shared) {
      return (
        <p className={styleClass.successMessage}>
          Din invitasjon om deling er sendt til{' '}
          <span className={FontWeight.FONT_BOLD}>
            {shareMethod === ShareMethods.EMAIL && email}
            {shareMethod === ShareMethods.SMS && cellPhone}
          </span>{' '}
          Invitasjonen er aktiv i 7 dager. Du kan når som helst endre delingen
          via Min side.
        </p>
      );
    }

    const { myPageUrl } = this.props.productInfo;
    return (
      <>
        <div className={styleClass.ingress}>
          Gi tilgang til {newspaperName} til din favorittperson
        </div>

        <div className={styleClass.invitationBox.root}>
          <p className={styleClass.invitationBox.text}>Send invitasjon på: </p>
          <div className={styleClass.invitationBox.selectBoxContainer}>
            <div
              className={styleClass.invitationBox.selectBox(
                this.state.shareMethod === ShareMethods.SMS,
              )}
              onClick={() => this.onChangeShareMethod(ShareMethods.SMS)}
            >
              SMS
            </div>
            <div
              className={styleClass.invitationBox.selectBox(
                this.state.shareMethod === ShareMethods.EMAIL,
              )}
              onClick={() => this.onChangeShareMethod(ShareMethods.EMAIL)}
            >
              E-post
            </div>
          </div>
        </div>

        {shareMethod === ShareMethods.EMAIL && (
          <>
            <InputField
              value={this.state.email}
              initialFocus={true}
              onChange={(e) => this.setState({ email: e.currentTarget.value })}
              formSubmitted={this.state.formSubmitted}
              label="E-postadresse"
              type={'email'}
              maxLength={320}
              validationErrorMessage="Vennligst oppgi en gyldig e-post adresse"
              validator={validateEmail}
              onEnter={this.shareProduct}
            />
            {validationError && (
              <div className={styleClass.errorText}>
                Det har dessverre skjedd en feil. Prøv gjerne igjen senere på{' '}
                <a href={myPageUrl} className={styleClass.linkText}>
                  Min side.
                </a>
              </div>
            )}
            <Button
              iconLeft={<Icon name={IconName.PLUS} />}
              onClick={this.shareProduct}
              disabled={!validateEmail(email)}
              text={'Gi tilgang'}
            />
          </>
        )}
        {this.state.shareMethod === ShareMethods.SMS && (
          <>
            <InputField
              value={this.state.cellPhone}
              initialFocus={true}
              onChange={(e) =>
                this.setState({ cellPhone: e.currentTarget.value })
              }
              formSubmitted={this.state.formSubmitted}
              label="Mobilnummer"
              type={'text'}
              maxLength={8}
              validationErrorMessage="Vennligst oppgi et gyldig telefonnummer"
              validator={validatePhoneNumber}
              onEnter={this.shareProduct}
            />
            {validationError && (
              <div className={styleClass.errorText}>
                Det har dessverre skjedd en feil. Prøv gjerne igjen senere på{' '}
                <a href={myPageUrl} className={styleClass.linkText}>
                  Min side.
                </a>
              </div>
            )}
            <Button
              iconLeft={<Icon name={IconName.PLUS} />}
              onClick={this.shareProduct}
              disabled={!validatePhoneNumber(cellPhone)}
              text={'Gi tilgang'}
            />
          </>
        )}
      </>
    );
  }

  private headingClicked() {
    this.setState({ clicked: !this.state.clicked });
    trackPulseEngagementEvent(this.props.userInfo, 'click show family sharing');
  }

  private shareProduct() {
    const { shareMethod, email, cellPhone } = this.state;
    if (shareMethod === ShareMethods.EMAIL && !validateEmail(email)) {
      this.setState({ formSubmitted: true });
      return;
    }

    if (shareMethod === ShareMethods.SMS && !validatePhoneNumber(cellPhone)) {
      this.setState({ formSubmitted: true });
      return;
    }
    this.setState({ validationError: false });
    const { sig, spidId } = this.props.userInfo;
    const storageData = getStorageData();
    (
      this.props.addFamilySharing(
        spidId,
        this.state.email,
        this.state.cellPhone,
        sig,
        storageData.referer,
        'FASTFLOW',
      ) as any
    )
      .then(() => {
        this.setState({ shared: true });
        this.trackSharedToFamily(`shared`);
      })
      .catch((res: ApiError) => {
        if (res.status === 409) {
          this.setState({ alreadyShared: true });
          this.trackSharedToFamily('Already shared');
        } else {
          this.setState({ validationError: true });
          this.trackSharedToFamily('Error');
        }
      });
  }

  private trackSharedToFamily(message: string) {
    const { shareMethod } = this.state;
    trackPulseEngagementEvent(this.props.userInfo, message, {
      'abo.ff.shareMethod': shareMethod,
    });
  }
}

export default connect<
  IStateProps,
  IActionProps,
  Pick<IProps, 'newspaperName'>
>(
  (state: IState) =>
    ({
      productInfo: state.productInfo,
      userInfo: state.userInfo,
    }) as IStateProps,
  (dispatch) =>
    bindActionCreators(
      {
        addFamilySharing,
        userEntersEmail,
      },
      dispatch,
    ) as IActionProps,
)(FamilySharing);
